import type { Scene } from '@lumen5/luminary';

/**
 * Reads a scene's raw scene-design config — the template's own defaults for every asset, before
 * anything the author changed. Returns null when the config can't be read (a Luminary without the
 * private accessor, or a design that fails to load) so callers skip the comparison rather than
 * crash.
 */
function getSceneDesignConfig(scene: Scene): unknown {
    try {
        const root = scene.root as unknown as {
            _getSceneDesignConfig?: (sceneDesignId: string, aspectRatio: string) => unknown;
            aspectRatio: string;
        };
        if (typeof root._getSceneDesignConfig !== 'function') {
            return null;
        }
        return root._getSceneDesignConfig(scene.sceneDesign, root.aspectRatio);
    } catch {
        return null;
    }
}

/**
 * Walks a scene-design config collecting one value per named asset. The config is an opaque nested
 * structure (asset groups, subslides, etc.), so every object is visited looking for the shape the
 * extractor recognizes rather than assuming a fixed tree layout. `extract` returns undefined for
 * nodes it doesn't care about.
 */
function collectByAssetName<T>(
    node: unknown,
    extract: (node: Record<string, unknown>) => T | undefined,
    map: Map<string, T>,
): void {
    if (!node || typeof node !== 'object') {
        return;
    }
    if (Array.isArray(node)) {
        node.forEach((child) => collectByAssetName(child, extract, map));
        return;
    }
    const obj = node as Record<string, unknown>;
    if (typeof obj.name === 'string') {
        const value = extract(obj);
        if (value !== undefined) {
            map.set(obj.name, value);
        }
    }
    Object.values(obj).forEach((value) => {
        if (value && typeof value === 'object') {
            collectByAssetName(value, extract, map);
        }
    });
}

/**
 * Map of asset name -> the scene design's default value for it, for whichever assets the extractor
 * recognizes. Returns null when the design config can't be read.
 */
export function getSceneDesignDefaults<T>(
    scene: Scene,
    extract: (node: Record<string, unknown>) => T | undefined,
): Map<string, T> | null {
    const config = getSceneDesignConfig(scene);
    if (config === null) {
        return null;
    }
    const map = new Map<string, T>();
    collectByAssetName(config, extract, map);
    return map;
}

/** Asset name -> the scene design's default text, for every named asset carrying text. */
export function getSceneDesignDefaultText(scene: Scene): Map<string, string> | null {
    return getSceneDesignDefaults(scene, (node) =>
        typeof node.text === 'string' ? node.text : undefined,
    );
}

/**
 * Asset name -> default text, limited to the slots an author can actually fill. Decorative text
 * baked into a design — the oversized quotation glyph on a quote layout, for instance — ships as
 * a named text asset with `isEditable: false`, so it has to be excluded when counting how many
 * slots a design expects the author to populate.
 */
export function getSceneDesignEditableDefaultText(scene: Scene): Map<string, string> | null {
    return getSceneDesignDefaults(scene, (node) =>
        typeof node.text === 'string' && node.isEditable !== false ? node.text : undefined,
    );
}

/** Asset name -> the scene design's default icon url, for every named asset carrying one. */
export function getSceneDesignDefaultIconUrls(scene: Scene): Map<string, string> | null {
    return getSceneDesignDefaults(scene, (node) => {
        const sourceData = node.sourceData as Record<string, unknown> | undefined;
        return sourceData && typeof sourceData.url === 'string' ? sourceData.url : undefined;
    });
}
